import { createContext, useContext, useState, useEffect } from "react";
import { API } from "@/App";

const SiteContentContext = createContext({
  content: {},
  loading: true,
  getContent: () => "",
  refreshContent: () => {},
});

export const useSiteContent = () => useContext(SiteContentContext);

// Use XMLHttpRequest to avoid body stream issues from fetch interceptors
const apiRequest = (url) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.timeout = 10000;
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, data });
      } catch (e) {
        resolve({ ok: false, data: [] });
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timed out'));
    xhr.send(null);
  });
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { ok, data } = await apiRequest(`${API}/content`);
      if (ok && Array.isArray(data)) {
        // Convert array to nested object: { page: { section: content } }
        const contentMap = {};
        data.forEach(item => {
          if (!contentMap[item.page]) {
            contentMap[item.page] = {};
          }
          contentMap[item.page][item.section] = item.content;
        });
        setContent(contentMap);
      }
    } catch (error) {
      console.error("Failed to load site content:", error);
    } finally {
      setLoading(false);
    }
  };

  const getContent = (page, section, defaultValue = "") => {
    return content[page]?.[section] || defaultValue;
  };

  const refreshContent = () => {
    fetchContent();
  };

  return (
    <SiteContentContext.Provider value={{ content, loading, getContent, refreshContent }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export default SiteContentContext;
